import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getThemeColors } from '../constants/theme';
import { debugError } from '../utils/debugLog';

// Theme types
export type ThemeMode = 'light' | 'dark';

export interface ThemeColors {
  // Background colors
  background: {
    primary: string;
    secondary: string;
    tertiary: string;
  };
  // Text colors
  text: {
    primary: string;
    secondary: string;
    tertiary: string;
    inverse: string;
  };
  // UI element colors
  border: {
    primary: string;
    secondary: string;
  };
  // Component-specific colors
  card: {
    background: string;
    border: string;
  };
  player: {
    background: string;
  };
}

export interface ThemeContextType {
  mode: ThemeMode;
  colors: ThemeColors;
  isLoading: boolean;
  error: string | null;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
}

// Default theme colors from centralized constants
const lightTheme: ThemeColors = getThemeColors('light');
const darkTheme: ThemeColors = getThemeColors('dark');

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>('dark'); // Default to dark (current design)
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get theme colors based on current mode
  const colors = mode === 'light' ? lightTheme : darkTheme;

  // Fetch theme configuration from Config API
  const fetchThemeConfig = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(
        'https://x37cfbpjfj.execute-api.eu-west-2.amazonaws.com/prd/config?station_id=eruption',
        {
          headers: {
            'x-api-key': process.env.REACT_APP_API_KEY as string,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Config API error: ${response.status}`);
      }

      const configData = await response.json();
      
      // Find the dark_light_mode configuration
      const themeConfig = configData.find((item: any) => item.key === 'dark_light_mode');
      
      if (themeConfig && themeConfig.string_value) {
        const apiTheme = themeConfig.string_value.toLowerCase();
        if (apiTheme === 'light' || apiTheme === 'dark') {
          setMode(apiTheme as ThemeMode);
        }
      }
    } catch (err) {
      debugError('Failed to fetch theme configuration:', err);
      setError(err instanceof Error ? err.message : 'Failed to load theme configuration');
      // Keep default dark theme on error
    } finally {
      setIsLoading(false);
    }
  };

  // Load theme configuration on mount
  useEffect(() => {
    fetchThemeConfig();
  }, []);

  // Apply theme to document root for CSS custom properties
  useEffect(() => {
    const root = document.documentElement;
    
    // Set CSS custom properties for theme colors
    root.style.setProperty('--theme-bg-primary', colors.background.primary);
    root.style.setProperty('--theme-bg-secondary', colors.background.secondary);
    root.style.setProperty('--theme-bg-tertiary', colors.background.tertiary);
    
    root.style.setProperty('--theme-text-primary', colors.text.primary);
    root.style.setProperty('--theme-text-secondary', colors.text.secondary);
    root.style.setProperty('--theme-text-tertiary', colors.text.tertiary);
    root.style.setProperty('--theme-text-inverse', colors.text.inverse);
    
    root.style.setProperty('--theme-border-primary', colors.border.primary);
    root.style.setProperty('--theme-border-secondary', colors.border.secondary);
    
    root.style.setProperty('--theme-card-bg', colors.card.background);
    root.style.setProperty('--theme-card-border', colors.card.border);
    
    root.style.setProperty('--theme-player-bg', colors.player.background);

    // Set data attribute for theme-aware styling
    root.setAttribute('data-theme', mode);
  }, [mode, colors]);

  const toggleTheme = () => {
    setMode(prevMode => prevMode === 'light' ? 'dark' : 'light');
  };

  const setTheme = (newMode: ThemeMode) => {
    setMode(newMode);
  };

  const value: ThemeContextType = {
    mode,
    colors,
    isLoading,
    error,
    toggleTheme,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
